import { FunctionComponent, useMemo } from "react";
import { Box } from "../box";
import { Group } from "../group";
import { Stack } from "../stack";
import { Text } from "../text";
import type {
    IContainerProps,
    IHeaderProps,
    ITitleProps,
    ISubtitleProps,
    IItemProps,
    IContentProps,
    IListProps,
    IInfoProps,
    IDropdownProps,
} from "./hover-card.types";
import { HoverCard as MantineHoverCard } from "@mantine/core";
import { INFO_COLORS } from "./hover-card.constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "../../inverted.scss";

/**
 * The default drop down styles are overriden so we can use the container component to style the hover card. Because when using the rechart tooltip, the Dropdown component is not used.
 */

export const Dropdown: FunctionComponent<IDropdownProps> = ({ children, allowClicking, containerProps, ...props }) => (
    <MantineHoverCard.Dropdown data-allow-clicking={allowClicking ?? false} {...props}>
        <Container {...containerProps}>{children}</Container>
    </MantineHoverCard.Dropdown>
);

/**
 * The hover card's box: padding, radius, shadow and the inverted Neutral/90 background.
 * @param {ReactNode} props.children - The hover card content.
 * @param {boolean} props.inverted - Gives text inside the Text/Inverse colours. Defaults to true unless `bg` is set.
 * @returns {JSX.Element} The Container component.
 */
export const Container: FunctionComponent<IContainerProps> = ({ children, style, inverted, ...props }) => (
    <Box
        bg="var(--mantine-color-neutral-9)"
        // The default background is the inverted Neutral/90; a container given its own background is not inverted
        data-inverted={inverted ?? typeof props.bg === "undefined"}
        p="lg"
        bdrs="md2"
        // An array, not a spread: a consumer's `style` may be a function or an array, which can't be object-spread.
        style={[{ boxShadow: "var(--mantine-shadow-md)" }, style]}
        miw={124}
        maw={460}
        {...props}
    >
        {children}
    </Box>
);

export const Header: FunctionComponent<IHeaderProps> = ({ subtitle, title }) => {
    return (
        <Stack gap="xs">
            {title && (
                <Title>
                    <Group gap="xs">{title}</Group>
                </Title>
            )}
            {subtitle && (
                <Subtitle>
                    <Group gap="xs">{subtitle}</Group>
                </Subtitle>
            )}
        </Stack>
    );
};
export const Title: FunctionComponent<ITitleProps> = ({ children }) => (
    <Text size="sm" variant="semibold" c="var(--mantine-color-title-1)">
        {children}
    </Text>
);

export const Subtitle: FunctionComponent<ISubtitleProps> = ({ children, ...props }) => (
    <Text size="sm" c="dimmed" {...props}>
        {children}
    </Text>
);

export const Item: FunctionComponent<IItemProps> = ({ color, label, value, subValue, isBold = false }) => {
    const labelElement = useMemo(
        () => (
            <Text size="sm" c="var(--mantine-color-title-1)" lineClamp={2} variant={isBold ? "semibold" : undefined}>
                {label}
            </Text>
        ),
        [label, isBold]
    );
    const valueElement = useMemo(
        () => (
            <Text size="sm" variant="semibold" c="var(--mantine-color-title-1)" ta="right">
                {value}
            </Text>
        ),
        [value]
    );
    return (
        <Group justify="space-between" gap="xl" wrap="nowrap">
            {color?.trim() ? (
                <Group gap="sm" miw={0} wrap="nowrap">
                    <Box
                        w={10}
                        h={10}
                        style={{
                            backgroundColor: color,
                            borderRadius: "50%",
                            flexShrink: 0,
                        }}
                    />
                    {labelElement}
                </Group>
            ) : (
                labelElement
            )}

            {subValue ? (
                <Group justify="flex-end" gap="sm" wrap="nowrap">
                    {valueElement}
                    <Text size="sm" variant="semibold" c="dimmed" ta="right">
                        {subValue}
                    </Text>
                </Group>
            ) : (
                valueElement
            )}
        </Group>
    );
};

export const Content: FunctionComponent<IContentProps> = ({ children }) => <Stack gap="xl">{children}</Stack>;

export const List: FunctionComponent<IListProps> = ({ children }) => <Stack gap="sm">{children}</Stack>;

export const Info: FunctionComponent<IInfoProps> = ({ children, icon, variant = "warning" }) => (
    <Group gap="xs" wrap="nowrap">
        {icon && (
            <Text size="sm" c={INFO_COLORS[variant]} style={{ flexShrink: 0 }}>
                <FontAwesomeIcon icon={icon} />
            </Text>
        )}
        <Text size="xs" variant="medium" c={INFO_COLORS[variant]}>
            {children}
        </Text>
    </Group>
);
